import { AJUDA_FINANCEIRO } from "@/lib/ajuda/dialogos/financeiro";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { FORMAS_DE_PAGAMENTO, FORMA_DE_PAGAMENTO_LABELS, hojeEmTexto, somarMeses, type DadosDaNota, type FormaDePagamento, type TipoFinanceiro } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { reais, textoParaCentavos, type LancamentoDaLista } from "@/lib/dinheiro";
import { campo, rotulo } from "../estilos";
import { NotaDoLancamento, type NotaDoLancamentoValor } from "./NotaDoLancamento";
import { DicaDaCategoria } from "./DicaDaCategoria";
import { DividirCompetencia } from "./DividirCompetencia";

const soDigitos = (v: string | null | undefined) => (v ?? "").replace(/\D/g, "");

type Props = { aberto: boolean; onFechar: () => void; competencia: string; editando?: LancamentoDaLista | null; tipoInicial?: TipoFinanceiro };

export function LancamentoDialog({ aberto, onFechar, competencia, editando, tipoInicial = "SAIDA" }: Props) {
  const utils = trpc.useUtils();
  const categorias = trpc.financeiro.categorias.listar.useQuery(undefined, { enabled: aberto }).data ?? [];
  const dreLigado = !!trpc.financeiro.configFinanceira.ler.useQuery(undefined, { enabled: aberto }).data?.dreLigado;
  const parceiros = trpc.financeiro.lancamentos.parceiros.useQuery(undefined, { enabled: aberto }).data ?? [];
  const [tipo, setTipo] = useState<TipoFinanceiro>(tipoInicial);
  const [descricao, setDescricao] = useState("");
  const [valor, setValor] = useState("");
  const [categoriaId, setCategoriaId] = useState("");
  const [mes, setMes] = useState(competencia);
  const [meses, setMeses] = useState(1);
  const [vencimento, setVencimento] = useState("");
  const [pago, setPago] = useState(false);
  const [pagoEm, setPagoEm] = useState(hojeEmTexto());
  const [forma, setForma] = useState<FormaDePagamento | "">("");
  const [partnerId, setPartnerId] = useState("");
  const [observacao, setObservacao] = useState("");
  const [nota, setNota] = useState<NotaDoLancamentoValor | null>(null);

  useEffect(() => {
    if (!aberto) return;
    const e = editando;
    setTipo(e?.tipo ?? tipoInicial);
    setDescricao(e?.descricao ?? "");
    setValor(e ? reais(e.valorCents) : "");
    setCategoriaId(e?.categoria.id ?? "");
    setMes(e?.competencia ?? competencia);
    setMeses(e?.mesesDeCompetencia ?? 1);
    setVencimento(e?.vencimento ?? "");
    setPago(!!e?.pagoEm);
    setPagoEm(e?.pagoEm ?? hojeEmTexto());
    setForma((e?.formaDePagamento as FormaDePagamento) ?? "");
    setPartnerId(e?.partner?.id ?? "");
    setObservacao(e?.observacao ?? "");
    setNota(null);
  }, [aberto, editando, competencia, tipoInicial]);

  // XML da nota: preenche o que estiver vazio ou for da nota (valor, mês, quem emitiu/recebeu).
  function aplicarDaNota(d: DadosDaNota) {
    const outro = tipo === "SAIDA" ? d.emitente : d.destinatario;
    if (d.valorCents) setValor(reais(d.valorCents));
    if (d.emitidaEm) setMes(d.emitidaEm.slice(0, 7));
    const doc = soDigitos(outro.documento);
    const achado = doc ? parceiros.find((p) => soDigitos(p.document) === doc) : undefined;
    if (achado) setPartnerId(achado.id);
    if (!descricao.trim() && d.numero) setDescricao(`NF ${d.numero}${outro.nome ? ` · ${outro.nome}` : ""}`.slice(0, 200));
  }

  const aoSalvar = {
    onSuccess: () => {
      toast.success(editando ? "Lançamento atualizado." : "Lançamento criado.");
      utils.financeiro.invalidate();
      onFechar();
    },
    onError: (e: { message: string }) => toast.error(e.message || "Não foi possível salvar."),
  };
  const criar = trpc.financeiro.lancamentos.criar.useMutation(aoSalvar);
  const editar = trpc.financeiro.lancamentos.editar.useMutation(aoSalvar);

  // A categoria das perdas é do sistema: só o "Dar como perdido" do A receber lança nela.
  const opcoes = categorias.filter((c) => c.tipo === tipo && c.linhaDoDre !== "PERDAS_COM_CLIENTES" && (c.ativa || c.id === categoriaId));
  const escolhida = categorias.find((c) => c.id === categoriaId);
  const linhaEscolhida = escolhida?.linhaDoDre;
  const escolherCategoria = (id: string) => {
    setCategoriaId(id);
    // Imposto sobre o faturamento é do mês anterior ao pagamento: sugere esse mês se ninguém mexeu.
    const linha = categorias.find((c) => c.id === id)?.linhaDoDre;
    if (dreLigado && !editando && linha === "DEDUCAO" && mes === competencia) setMes(somarMeses(competencia, -1));
  };
  const valorCents = textoParaCentavos(valor);
  const valido = descricao.trim().length >= 2 && valorCents > 0 && !!categoriaId && !!mes && (!nota || nota.numero.trim().length > 0);

  const salvar = () => {
    const dados = {
      descricao,
      valorCents,
      competencia: mes,
      mesesDeCompetencia: meses,
      categoriaId,
      vencimento: vencimento || null,
      pagoEm: pago ? pagoEm : null,
      formaDePagamento: pago && forma ? forma : null,
      partnerId: partnerId || null,
      observacao: observacao || null,
    };
    if (editando) editar.mutate({ id: editando.id, ...dados });
    else
      criar.mutate({
        tipo,
        ...dados,
        ...(nota
          ? { nota: { numero: nota.numero.trim(), serie: nota.serie, chaveDeAcesso: nota.chaveDeAcesso, emitidaEm: nota.emitidaEm, fileIds: nota.arquivos.map((a) => a.fileId) } }
          : {}),
      });
  };
  const salvando = criar.isPending || editar.isPending;

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="max-h-[90dvh] max-w-lg overflow-y-auto border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_FINANCEIRO.lancamento}>{editando ? "Editar lançamento" : "Novo lançamento"}</DialogTitle>
          <DialogDescription>Receitas de pedidos entram sozinhas; aqui vai o que o sistema não sabe.</DialogDescription>
        </DialogHeader>

        {!editando && (
          <div className="grid grid-cols-2 gap-1 rounded-lg bg-zinc-100 p-1 dark:bg-muted">
            {(["SAIDA", "ENTRADA"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setTipo(t);
                  setCategoriaId("");
                }}
                className={cn(
                  "rounded-md py-1.5 text-sm font-semibold",
                  tipo === t ? "bg-white text-zinc-900 shadow-sm dark:bg-card dark:text-foreground" : "text-zinc-500 dark:text-muted-foreground",
                )}
              >
                {t === "SAIDA" ? "Saída" : "Entrada"}
              </button>
            ))}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-12">
          <label className="sm:col-span-12">
            <span className={rotulo}>Descrição</span>
            <input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex: Aluguel da sala" className={campo} />
          </label>
          <label className="sm:col-span-5">
            <span className={rotulo}>Valor</span>
            <input value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" placeholder="0,00" className={campo} />
          </label>
          <label className="sm:col-span-7">
            <span className={rotulo}>Categoria</span>
            <select value={categoriaId} onChange={(e) => escolherCategoria(e.target.value)} className={campo}>
              <option value="">Escolha</option>
              {opcoes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
            <DicaDaCategoria linha={linhaEscolhida} dreLigado={dreLigado} nome={escolhida?.nome} />
          </label>
          <label className="sm:col-span-6">
            <span className={rotulo}>Mês de competência</span>
            <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className={campo} />
          </label>
          <label className="sm:col-span-6">
            <span className={rotulo}>Vencimento</span>
            <input type="date" value={vencimento} onChange={(e) => setVencimento(e.target.value)} className={campo} />
          </label>
          {dreLigado && tipo === "SAIDA" && <DividirCompetencia meses={meses} onChange={setMeses} valorCents={valorCents} competencia={mes} />}
          <label className="sm:col-span-12">
            <span className={rotulo}>{tipo === "SAIDA" ? "Fornecedor" : "Cliente"} (opcional)</span>
            <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)} className={campo}>
              <option value="">Nenhum</option>
              {parceiros.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-foreground">
          <input type="checkbox" checked={pago} onChange={(e) => setPago(e.target.checked)} className="h-4 w-4 rounded border-zinc-300 text-brand-600" />
          {tipo === "SAIDA" ? "Já foi pago" : "Já foi recebido"}
        </label>
        {pago && (
          <div className="grid gap-3 sm:grid-cols-2">
            <label>
              <span className={rotulo}>Data</span>
              <input type="date" value={pagoEm} onChange={(e) => setPagoEm(e.target.value)} className={campo} />
            </label>
            <label>
              <span className={rotulo}>Forma</span>
              <select value={forma} onChange={(e) => setForma(e.target.value as FormaDePagamento | "")} className={campo}>
                <option value="">Não informada</option>
                {FORMAS_DE_PAGAMENTO.map((f) => (
                  <option key={f} value={f}>
                    {FORMA_DE_PAGAMENTO_LABELS[f]}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {editando ? (
          editando._count.notas > 0 && <p className="text-xs text-zinc-500 dark:text-muted-foreground">Tem nota fiscal ligada: veja e troque na aba Notas fiscais.</p>
        ) : (
          <NotaDoLancamento nota={nota} onChange={setNota} onLido={aplicarDaNota} />
        )}

        <label>
          <span className={rotulo}>Observação</span>
          <textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} rows={2} className={cn(campo, "h-auto py-2")} />
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onFechar} className="h-9 rounded-md px-4 text-sm text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted">
            Cancelar
          </button>
          <button
            type="button"
            disabled={!valido || salvando}
            onClick={salvar}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
